/**
 * depositAgreement.ts — what the deposit sheet's Agreement block is allowed to offer.
 *
 * THE DEPOSIT ARC IS THE ONE THAT RUNS AHEAD OF ITS CONTRACT. A deposit is routinely paid
 * weeks before a vehicle is allocated: there is no contract, no driver row and no account,
 * so the person who paid it cannot be reached through the driver app and cannot be named by
 * a foreign key. Everything unusual in this file follows from that single fact —
 *
 *   the depositor is identified by a NAME and PHONE an admin types, not by an id;
 *   the depositor signs through a shared LINK, like the witness does;
 *   and the depositor is the FIRST signature, unlike the witness, so nothing is frozen when
 *   the link is issued and no reviewed hash is required to issue it.
 *
 * The board's own deposit row keeps working for deposits that do have a contract — same
 * document, same arc, read two ways — so the rules here are deliberately the board's rules
 * with the witness half removed, rather than a second opinion about the same states.
 */

import { sealStateFor, type SealAttempt, type SealState } from '@/components/agreements/boardSlots';
import type { AgreementDocumentRow, AgreementSignatureRow, DepositAgreementArc } from '@/lib/agreements/api';

/** The single primary button the block shows, or none. */
export type DepositAgreementAction = 'create' | 'link' | 'countersign' | 'seal' | null;

export interface DepositAgreementInput {
    /** `contract_deposits.contract_id` — null while the deposit belongs to no contract. */
    contractId: number | null;
    /** The driver on that contract, when there is one. Prefill only; never authority. */
    driverName: string | null;
    driverPhone: string | null;
    arc: DepositAgreementArc | null;
    document: AgreementDocumentRow | null;
    /** `agreement_render`'s `missing[]`, once the document exists. */
    missing: string[] | null;
    /** What THIS SESSION knows about a finalize call for the document. */
    sealAttempt: SealAttempt | null;
}

export interface DepositAgreementState {
    document: AgreementDocumentRow | null;
    /** The signature that is not the company's — the depositor's. */
    depositor: AgreementSignatureRow | null;
    company: AgreementSignatureRow | null;
    /** A paper signature is on file and no in-app document exists. */
    scanned: boolean;
    seal: SealState;
    /** Whether this deposit belongs to a contract yet. Nothing here waits on it. */
    attachedToContract: boolean;
    /**
     * What to put in the create form's fields before an admin types.
     *
     * A PREFILL, NOT AN IDENTITY. The create form asks for both fields on every deposit —
     * see the note below on whose rule that is — so these are a convenience where a contract
     * driver happens to be known, and blank where nobody is. Whoever is named is who the
     * document is addressed to.
     */
    depositorPrefill: DepositorDraft;
    /** A merge field is unresolved, so the document would print a blank line to the depositor. */
    blockedByMerge: boolean;
    canIssueLink: boolean;
    countersignReady: boolean;
    primary: DepositAgreementAction;
    statusLine: string;
}

/** What the create form holds while it is being typed into. */
export interface DepositorDraft {
    name: string;
    phone: string;
    address: string;
}

/** How the depositor is referred to in the status line before the document names them. */
function depositorLabel(depositor: AgreementSignatureRow | null): string {
    return depositor?.signer_name?.trim() || 'the depositor';
}

export function depositAgreementState(input: DepositAgreementInput): DepositAgreementState {
    const doc = input.document;
    const signatures = doc?.signatures ?? [];

    const company = signatures.find((signature) => signature.party === 'COMPANY_REP') ?? null;
    // Party-agnostic on purpose: the deposit template signs DRIVER first today, and a future
    // version that renames that seat should still land the depositor in the depositor's chip.
    const depositor = signatures.find((signature) => signature.party !== 'COMPANY_REP') ?? null;

    const scanned = !doc && !!input.arc?.signed_at;
    const seal = sealStateFor(doc, input.sealAttempt);
    const blockedByMerge = (input.missing?.length ?? 0) > 0;
    const declined = signatures.some((signature) => signature.status === 'DECLINED');
    const completed = doc?.status === 'COMPLETED';

    const countersignReady =
        !!doc &&
        doc.status === 'IN_SIGNING' &&
        company?.status !== 'SIGNED' &&
        !!depositor &&
        depositor.status === 'SIGNED';

    // PENDING_REVIEW COUNTS, AND THAT IS THE DIFFERENCE FROM THE WITNESS LINK. The depositor
    // is the first signer, so there is nothing yet to attest to and nothing to freeze against;
    // `agreement_token_sign` freezes against what the depositor's own browser rendered.
    const canIssueLink =
        !!doc &&
        (doc.status === 'PENDING_REVIEW' || doc.status === 'IN_SIGNING') &&
        !!depositor &&
        depositor.status !== 'SIGNED' &&
        depositor.status !== 'VOIDED' &&
        !blockedByMerge;

    const awaitingSeal = seal.phase === 'sealing' || seal.phase === 'failed' || seal.phase === 'ready';

    let primary: DepositAgreementAction;
    if (!doc && !scanned) primary = 'create';
    else if (countersignReady) primary = 'countersign';
    else if (awaitingSeal) primary = 'seal';
    else if (canIssueLink) primary = 'link';
    else primary = null;

    let statusLine: string;
    if (scanned) statusLine = 'Signed on paper.';
    else if (!doc) statusLine = 'No deposit agreement yet.';
    else if (declined) statusLine = `${depositorLabel(depositor)} declined — void and reissue to recover.`;
    else if (completed && doc.finalized_at) statusLine = 'Signed by the depositor and Primelane.';
    else if (seal.phase === 'sealing') statusLine = 'Both parties signed — sealing the executed PDF.';
    else if (seal.phase === 'failed') statusLine = 'Both parties signed — sealing failed.';
    else if (seal.phase === 'ready') statusLine = 'Both parties signed — ready to finalize.';
    else if (countersignReady) statusLine = 'Waiting on your countersignature.';
    else if (blockedByMerge) statusLine = 'Cannot go out until the merge fields resolve.';
    else statusLine = `Waiting on ${depositorLabel(depositor)} to sign the link.`;

    return {
        document: doc,
        depositor,
        company,
        scanned,
        seal,
        attachedToContract: input.contractId !== null,
        depositorPrefill: {
            name: (input.driverName ?? '').trim(),
            phone: (input.driverPhone ?? '').trim(),
            // NO ADDRESS PREFILL, AND THAT IS THE OWNER'S RULING RATHER THAN A GAP. The address on
            // this form is a MANUAL capture with no profile-derived source: prefilling it from the
            // contract driver's `profiles.address` would be exactly the profile-derived source the
            // ruling excludes, arriving by a different route and pre-approved by a field an admin
            // never had to look at. The name and phone are prefilled because they are the
            // document's ADDRESSEE and the RPC's own rule already reads them off the contract where
            // there is one; the address is not, so it is typed or it is left to the profile.
            address: '',
        },
        blockedByMerge,
        canIssueLink,
        countersignReady,
        primary,
        statusLine,
    };
}

export interface DepositAgreementBeatInput {
    /**
     * The legacy `contract_deposit_agreements` row. Still read, because the paper path writes
     * nothing else — and because it is all a caller holds until the document read resolves.
     */
    arc: Pick<DepositAgreementArc, 'signed_at' | 'agreement_url'> | null;
    document: AgreementDocumentRow | null;
}

/**
 * Which beat of the lifecycle strip this deposit agreement is on: a 0-based index into
 * `Create · Send link · Depositor signs · Countersign & seal`, and 4 when every one of them is
 * behind it and none is current.
 *
 * THE BEAT IS SIGNATURE PROGRESS, NOT THE LEGACY ARC. `contract_deposit_agreements` writes
 * `signed_at` at the COUNTERSIGNATURE and `agreement_url` at the seal, so a document the
 * depositor has already signed through their own link reads there exactly like one created a
 * minute ago — which is what left the strip pointing at `Send link` while the block underneath
 * said `Waiting on your countersignature`. The document's signature rows carry the two moves
 * the arc cannot see: `agreement_issue_token` takes the depositor PENDING → SENT, opening the
 * link takes it → VIEWED, and signing takes it → SIGNED.
 *
 * THE LAST BEAT IS TWO ACTS, SO IT STAYS CURRENT ACROSS BOTH. `Countersign & seal` is where the
 * document sits from the depositor's signature until the executed PDF is stored, and only a
 * finalized document has nothing left to do.
 */
export function depositAgreementBeat(input: DepositAgreementBeatInput): number {
    const doc = input.document;

    // No live document: the arc alone, which is coarse but is the whole truth for a paper
    // signing — and an arc with no signature and nothing live to send is back at Create.
    if (!doc) {
        const arc = input.arc;
        if (arc?.signed_at && arc.agreement_url) return 4;
        if (arc?.signed_at) return 3;
        return 0;
    }

    if (doc.status === 'COMPLETED') return doc.finalized_at ? 4 : 3;

    // Party-agnostic, exactly as the block reads it above.
    const depositor = doc.signatures.find((signature) => signature.party !== 'COMPANY_REP') ?? null;
    if (depositor?.status === 'SIGNED') return 3;
    if (depositor?.status === 'SENT' || depositor?.status === 'VIEWED') return 2;
    return 1;
}

/**
 * The driver name carried on a deposit row, assembled the way every other deposit surface
 * assembles it. Returns null rather than an empty string so a caller can tell "no driver"
 * from "a driver whose name is blank".
 */
export function depositDriverName(deposit: {
    driver_first_name: string | null;
    driver_middle_name: string | null;
    driver_last_name: string | null;
}): string | null {
    const name = [deposit.driver_first_name, deposit.driver_middle_name, deposit.driver_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
    return name || null;
}

// ─── The identity the document freezes ───────────────────────────────────────
//
// THE THREE FIELDS ARE REQUIRED BY THREE DIFFERENT AUTHORITIES, AND CONFLATING THEM IS HOW
// A CLIENT ENDS UP STRICTER THAN THE DATABASE WITHOUT MEANING TO.
//
//   THE NAME is `agreement_create`'s own rule, and only on a deposit with NO contract:
//   AGREEMENT_SIGNER_REQUIRED is raised there because nothing else can name the signer. On
//   an attached deposit the RPC asks for nothing, and where the contract driver holds an
//   account it ignores all three parameters and binds the seat to that profile instead.
//
//   THE PHONE is the TEMPLATE's rule. `driver.full_name` and `driver.phone` are both
//   required merge fields on the deposit template, and on a pre-contract deposit both are
//   read back off the frozen signature row rather than out of any table — so a document
//   created without a phone is ACCEPTED by the RPC and then reports it in `missing[]`,
//   prints a blank line, and cannot be sent until it is voided.
//
//   THE ADDRESS is the INSTRUMENT's rule, and it is the reason the field exists at all: a
//   deposit schedule states where the party it is addressed to lives, and a payer at
//   application stage has no `profiles` row for `driver.address` to resolve from. What is
//   typed here is frozen onto their signature seat at create and is the only address that
//   key can ever answer for them.
//
// The form therefore asks for all three and DEMANDS the first two, which is stricter than SQL
// on purpose: an admin is stopped by a disabled button rather than by a document that has to
// be thrown away.
//
// THE ADDRESS IS THE ONE IT DEMANDS ONLY WHERE IT IS THE ONLY SOURCE, and that is the
// difference between asking and nagging. On a deposit that HAS a contract, `agreement_create`
// binds the seat to the driver's profile and DISCARDS all three typed parameters, so
// `driver.address` resolves from that profile and a typed one is thrown away — 219 of the
// live project's 253 deposits are in that state, and every one of their drivers carries an
// address. Demanding it there would be typing that changes nothing on every deposit an admin
// papers from the setup canvas. On the 34 that have NO contract there is no profile and no
// other record, so it is required — which is exactly the boundary the RPC itself draws when
// it raises AGREEMENT_SIGNER_REQUIRED. It is offered in both cases, because the person who
// actually paid is not always the person the contract ends up naming.

/** Everything but digits; a leading + is not part of the count either way. */
function digitsIn(value: string): string {
    return value.replace(/\D/g, '');
}

/** The shortest and longest national/international forms worth accepting. */
const MIN_PHONE_DIGITS = 9;
const MAX_PHONE_DIGITS = 15;

export function depositorNameError(name: string): string | null {
    const trimmed = name.trim();
    if (!trimmed) return 'The agreement prints this name — enter who paid the deposit.';
    if (trimmed.length < 2) return 'Enter the full name as it should appear on the agreement.';
    return null;
}

/**
 * A phone check that refuses what is obviously not a number and accepts every way a real one
 * is written. Ghana's national form is ten digits (024 000 0000) and its international one
 * twelve (233 24 000 0000), and admins type both, with and without spaces, dashes and a
 * leading +. Anything from nine to fifteen digits is somebody's phone number somewhere; a
 * stricter pattern here would reject real input the RPC would have accepted.
 */
export function depositorPhoneError(phone: string): string | null {
    const digits = digitsIn(phone);
    if (!digits) return 'The agreement prints this number — enter the depositor’s phone.';
    if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
        return 'That does not look like a phone number — e.g. 024 000 0000 or +233 24 000 0000.';
    }
    return null;
}

/**
 * An address check with a floor and no pattern. Ghanaian addresses are written every way there
 * is — a digital address (GA-492-1234), a house number and a street, a landmark and a suburb —
 * so anything that tried to recognise the SHAPE of one would reject real addresses the RPC
 * accepts and the document would print. What is worth refusing is a field somebody tabbed
 * through: an empty one, and a couple of characters that cannot be anywhere.
 */
export function depositorAddressError(address: string, required: boolean): string | null {
    const trimmed = address.trim();
    if (!trimmed) {
        return required
            ? 'The agreement prints this address — enter where the depositor lives.'
            : null;
    }
    if (trimmed.length < 5) return 'Enter the address as it should appear on the agreement.';
    return null;
}

/**
 * Whether the create form has enough to call `agreement_create`.
 *
 * `addressRequired` is `true` exactly where the typed address is the only one that exists —
 * a deposit with no contract — and false where the contract's driver answers for it.
 */
export function canCreateDepositAgreement(draft: DepositorDraft, addressRequired: boolean): boolean {
    return (
        depositorNameError(draft.name) === null &&
        depositorPhoneError(draft.phone) === null &&
        depositorAddressError(draft.address, addressRequired) === null
    );
}
